/**
 * Centralized customer session management.
 * ALL session keys must be cleared here — used by logout, auth guard, and token expiry handling.
 */

const SESSION_KEYS = ['auth_token', 'refresh_token', 'user', 'lastOrderId'] as const
const ADMIN_SESSION_KEYS = ['admin_token', 'admin_refresh_token', 'admin_user', 'admin_last_activity'] as const

const AUTH_LOST_EVENT = 'ttelgo:auth-lost'

export const clearSession = (): void => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key))
}

export const clearAdminSession = (): void => {
  ADMIN_SESSION_KEYS.forEach((key) => localStorage.removeItem(key))
}

export const getAuthToken = (): string | null => localStorage.getItem('auth_token')

export const getRefreshToken = (): string | null => localStorage.getItem('refresh_token')

export const getAdminToken = (): string | null => localStorage.getItem('admin_token')

export const getAdminRefreshToken = (): string | null => localStorage.getItem('admin_refresh_token')

export const setAuthTokens = (accessToken: string, refreshToken?: string | null): void => {
  if (accessToken?.trim()) {
    localStorage.setItem('auth_token', accessToken.trim())
  }
  if (refreshToken?.trim()) {
    localStorage.setItem('refresh_token', refreshToken.trim())
  }
}

export const setAdminTokens = (accessToken: string, refreshToken?: string | null): void => {
  if (accessToken?.trim()) {
    localStorage.setItem('admin_token', accessToken.trim())
  }
  if (refreshToken?.trim()) {
    localStorage.setItem('admin_refresh_token', refreshToken.trim())
  }
}

/** Decode JWT payload without verifying signature (UX / expiry checks only). */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const json = atob(padded)
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

export const getTokenExpiryMs = (token: string | null | undefined): number | null => {
  if (!token) return null
  const payload = decodeJwtPayload(token)
  const exp = payload?.exp
  if (typeof exp !== 'number') return null
  return exp * 1000
}

export const isAccessTokenExpired = (token: string | null | undefined, skewMs = 5_000): boolean => {
  if (!token) return true
  const expMs = getTokenExpiryMs(token)
  if (expMs == null) return false // opaque / non-JWT — treat as present
  return Date.now() >= expMs - skewMs
}

export const isRefreshTokenExpired = (token: string | null | undefined, skewMs = 5_000): boolean => {
  return isAccessTokenExpired(token, skewMs)
}

/**
 * Recoverable session: valid access OR non-expired refresh that can renew access.
 */
export const hasRecoverableSession = (): boolean => {
  const access = getAuthToken()
  if (access && !isAccessTokenExpired(access)) return true
  const refresh = getRefreshToken()
  return !!refresh && !isRefreshTokenExpired(refresh)
}

/** UI "logged in" chrome — recoverable session, not merely a stale access string. */
export const isAuthenticated = (): boolean => hasRecoverableSession()

export const notifyAuthLost = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_LOST_EVENT))
  }
}

export const onAuthLost = (handler: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined
  const listener = () => handler()
  window.addEventListener(AUTH_LOST_EVENT, listener)
  return () => window.removeEventListener(AUTH_LOST_EVENT, listener)
}

export { AUTH_LOST_EVENT }
